/**
 * How a patient's STAY reads, wherever it is shown: status badge, stay card,
 * timeline, and the leaf-unit / active-encounter / current-location rules the
 * placement-event log implies.
 *
 * PLATFORM, not an agent's. A visit and its placements are platform data, so
 * this sits with the other clinical primitives rather than in the census
 * board's module: the org admin's location dialogs and AssignPatientDialog
 * read it too, and none of them is the patient-admin agent. The census board
 * (apps/patient-admin) and its DEV preview harness are two more callers, which
 * is what keeps "is this patient currently admitted" to one definition.
 */

import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { humaniseAttributeKey } from '@/lib/patientLabel'
import type { Location } from '@/api/endpoints/org'
import type { StayWithPlacements } from '@/hooks/useEncounters'

/** Leaf units only — placement is leaf-only server-side (409 LOCATION_HAS_CHILDREN
 *  otherwise); a unit that is any other unit's parent is filtered out. */
export function leafLocations(locations: Location[] | undefined): Location[] {
  if (!locations) return []
  const parentIds = new Set(locations.map((l) => l.parent_id).filter(Boolean) as string[])
  return locations.filter((l) => !parentIds.has(l.id))
}

/** The patient's admitted stay, wherever it sits in the list; undefined when
 *  they have none. Shared by the census board and its DEV preview so "is this
 *  patient currently admitted" has one definition.
 *
 *  Found by STATUS, not at index 0. The list is newest-first, and newest is not
 *  the same fact as open: a planned future admission sorts ahead of the current
 *  one, and a readmitted patient carries a discharged stay ahead of the open
 *  one. Reading index 0 rendered an admitted patient, physically in a bed, as
 *  not admitted, which suppressed their unit, their admission date and their
 *  day count, and left the allocation dialogs unable to resolve their unit.
 *  The server admits at most one stay at a time, so this matches one row. */
export function activeEncounter(encounters: StayWithPlacements[] | undefined): StayWithPlacements | undefined {
  return encounters?.find((e) => e.status === 'admitted')
}

/** The patient's current unit id, or null when not currently admitted.
 *
 *  Read straight off the stay. It used to be derived by walking the placement
 *  log for the latest non-null location, because the retired encounters shape
 *  had no current-location field; a stay carries `location` directly, so the
 *  derivation, and the chance of it disagreeing with the server, is gone. */
export function currentLocationId(encounters: StayWithPlacements[] | undefined): string | null {
  return activeEncounter(encounters)?.location ?? null
}

export function locationIndex(locations: Location[] | undefined): Map<string, Location> {
  const map = new Map<string, Location>()
  for (const l of locations ?? []) map.set(l.id, l)
  return map
}

/**
 * A stay moment as a nurse reads it: date and time to the MINUTE.
 *
 * Replaces a bare `toLocaleString()`, which printed seconds — "13.8.2026,
 * 03:42:11". Nothing about an admission is meaningful to the second, and a
 * timestamp carrying more precision than the fact behind it warrants is a
 * machine talking to itself.
 */
export function formatStayMoment(iso: string, locale?: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso
  return at.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })
}

/**
 * Whole days elapsed since admission — 0 on the day of admission.
 *
 * ORIENTATION ONLY. This is wall-clock elapsed time, so a nurse can see at a
 * glance how long someone has been in. It is NOT a treatment-day count: the
 * billing day of ADR-0003 (the CHOP 99.C treatment day, the LTC census day) is
 * derived from the placement log against the tenant's regime, is recorded as
 * derived, and is not this number. Do not wire this to anything billing.
 */
export function stayDayCount(admittedAt: string, now: Date = new Date()): number | null {
  const from = new Date(admittedAt)
  if (Number.isNaN(from.getTime())) return null
  const days = Math.floor((now.getTime() - from.getTime()) / 86_400_000)
  return days < 0 ? 0 : days
}

interface StayHeaderProps {
  /** The patient's open stay, or undefined when they are not currently
   *  admitted — the header states that case rather than rendering nothing. */
  encounter: StayWithPlacements | undefined
  /** Current unit name, resolved by the caller from the PLACEMENT-EVENT LOG
   *  (currentLocationId), never from a registration attribute. */
  unitName?: string
  title: string
}

/**
 * The stay, stated as a stay: status, where they are, and how long they have
 * been in — "aufgenommen seit 3 Tagen (13. Aug. 2026, 03:42)".
 *
 * This surface OPENS the episode (ADR-0003: the encounter is the billing
 * episode in acute, LTC and ambulatory settings), and before this it showed
 * only a raw admission timestamp in a row labelled with the unit — so an open
 * stay read as an incidental attribute of a patient rather than as the thing
 * this agent exists to open and close.
 */
export function StayHeader({ encounter, unitName, title }: StayHeaderProps) {
  const { t, i18n } = useTranslation()
  const days = encounter ? stayDayCount(encounter.started_at) : null

  return (
    <div className="mb-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-[17px] font-semibold tracking-tight text-neutral-900">
          {title}
        </h3>
        <EncounterStatusBadge status={encounter ? 'admitted' : 'not_admitted'} />
      </div>
      {encounter && (
        <p className="mt-1 text-[15px] text-neutral-600">
          {unitName && <span className="font-semibold text-neutral-900">{unitName}</span>}
          {unitName && ' · '}
          {days === null
            ? formatStayMoment(encounter.started_at, i18n.language)
            : t(days === 0 ? 'patientAdmin.detail.admittedToday' : 'patientAdmin.detail.admittedSince', {
                count: days,
                at: formatStayMoment(encounter.started_at, i18n.language),
              })}
        </p>
      )}
      {/* ADR-0003 slot — `encounters.episode_ref` (the tenant's opaque episode
          identifier: the acute Fallnummer-resolvable case reference, the LTC
          residency id, the Spitex authorisation) belongs on this line, beside
          the stay it identifies. The field is not on the encounter contract
          yet (one migration, per that ADR's Consequences); when it lands it
          renders here and nowhere else — it is never put on an effort entry. */}
    </div>
  )
}

/** A localized label for a config-driven enum code, falling back to a
 *  humanised form of the raw code so an unrecognised deployment value never
 *  renders a raw i18n key. */
export function useEnumLabel() {
  const { t } = useTranslation()
  return (namespace: string, code: string) =>
    t(`patientAdmin.${namespace}.${code}`, { defaultValue: humaniseAttributeKey(code) })
}

export function EncounterStatusBadge({ status }: { status: string }) {
  const label = useEnumLabel()
  const tone =
    status === 'admitted'
      ? 'bg-tone-ok-tint text-tone-ok'
      : status === 'cancelled'
        ? 'bg-neutral-100 text-neutral-500'
        : 'bg-neutral-100 text-neutral-700'
  return (
    <span className={cn('rounded-full px-2.5 py-0.5 text-[12px] font-semibold', tone)}>
      {label('status', status)}
    </span>
  )
}

interface EncounterCardProps {
  encounter: StayWithPlacements
  locationById: Map<string, Location>
}

/** One stay (FHIR Encounter) card: class/status header, period + disposition,
 *  and its append-only placement-event timeline. Pure presentational — reused
 *  by the live stay history and the DEV preview fixture render. */
export function EncounterCard({ encounter: e, locationById }: EncounterCardProps) {
  const { t, i18n } = useTranslation()
  const label = useEnumLabel()

  return (
    <div className="rounded-[18px] border border-neutral-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[15px] font-semibold text-neutral-900">{label('class', e.class)}</span>
        <EncounterStatusBadge status={e.status} />
      </div>
      <div className="mt-1 space-y-0.5 text-[13px] text-neutral-500">
        <p>
          {t('patientAdmin.detail.admittedAt')}: {formatStayMoment(e.started_at, i18n.language)}
        </p>
        {e.ended_at && (
          <p>
            {t('patientAdmin.detail.dischargedAt')}: {formatStayMoment(e.ended_at, i18n.language)}
          </p>
        )}
        {e.disposition && (
          <p>
            {t('patientAdmin.detail.disposition')}: {label('disposition', e.disposition)}
          </p>
        )}
        {e.responsible_service && (
          <p>
            {t('patientAdmin.detail.responsibleService')}: {e.responsible_service}
          </p>
        )}
      </div>
      {e.placements.length > 0 && (
        <ol className="mt-3 space-y-1 border-t border-neutral-100 pt-2">
          {e.placements.map((ev) => (
            <li key={ev.id} className="flex items-center justify-between text-[13px] text-neutral-600">
              <span>
                {label('eventType', ev.kind)}
                {ev.location && `: ${locationById.get(ev.location)?.name ?? ev.location}`}
              </span>
              <span className="text-neutral-400">{formatStayMoment(ev.recorded_at, i18n.language)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

interface StayTimelineProps {
  encounters: StayWithPlacements[]
  locationById: Map<string, Location>
}

/** Stay history as a vertical timeline: one dot per stay (newest at top,
 *  matching the encounters-newest-first contract), joined by a connector
 *  line. The line is a flex sibling of the dot that stretches to fill the
 *  row's own height — no absolute-position math against a card's variable
 *  height. Shared by the live census board and its DEV preview so the two
 *  never drift into different stay-history treatments. */
export function StayTimeline({ encounters, locationById }: StayTimelineProps) {
  return (
    <ol>
      {encounters.map((e, i) => {
        const isLast = i === encounters.length - 1
        return (
          <li key={e.id} className="flex gap-3">
            <div className="flex flex-col items-center" aria-hidden="true">
              <span
                className={cn(
                  'mt-2 h-2.5 w-2.5 shrink-0 rounded-full',
                  e.status === 'admitted' ? 'bg-tone-ok' : 'bg-neutral-300',
                )}
              />
              {!isLast && <span className="mt-1 w-px flex-1 bg-neutral-200" />}
            </div>
            <div className={cn('min-w-0 flex-1', !isLast && 'pb-4')}>
              <EncounterCard encounter={e} locationById={locationById} />
            </div>
          </li>
        )
      })}
    </ol>
  )
}
